import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import Seo, { breadcrumbLd } from "@/components/vr/Seo";
import { Clock, Play, ChevronLeft, ChevronRight, RotateCcw, Trophy, CheckCircle2, XCircle, AlertTriangle, BookOpen, ArrowLeft } from "lucide-react";

export function QuizList() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [quizzes, setQuizzes] = useState([]);

  const [step, setStep] = useState(1); // 1=class, 2=subject, 3=chapter, 4=quizzes
  const [selClass, setSelClass] = useState(null);   // full doc
  const [selSubject, setSelSubject] = useState(null);
  const [selChapter, setSelChapter] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)); }, []);

  const pickClass = (c) => {
    setSelClass(c); setSelSubject(null); setSelChapter(null);
    setSubjects([]); setChapters([]); setQuizzes([]);
    setLoading(true);
    api.get(`/subjects?class_slug=${c.slug}`).then((r) => setSubjects(r.data)).finally(() => setLoading(false));
    setStep(2);
  };

  const pickSubject = (s) => {
    setSelSubject(s); setSelChapter(null);
    setChapters([]); setQuizzes([]);
    setLoading(true);
    api.get(`/chapters?class_slug=${selClass.slug}&subject_slug=${s.slug}`).then((r) => setChapters(r.data)).finally(() => setLoading(false));
    setStep(3);
  };

  const pickChapter = (ch) => {
    setSelChapter(ch);
    setLoading(true);
    api.get(`/quizzes?class_slug=${selClass.slug}&subject_slug=${selSubject.slug}&chapter_slug=${ch.slug}`)
      .then((r) => setQuizzes(r.data)).finally(() => setLoading(false));
    setStep(4);
  };

  const goBack = () => {
    if (step === 4) { setStep(3); setQuizzes([]); }
    else if (step === 3) { setStep(2); setSelSubject(null); setChapters([]); setQuizzes([]); }
    else if (step === 2) { setStep(1); setSelClass(null); setSubjects([]); setChapters([]); setQuizzes([]); }
  };

  const crumbTrail = [
    { label: "MCQ Quiz", to: "/mcq" },
    ...(selClass ? [{ label: selClass.name }] : []),
    ...(selSubject ? [{ label: selSubject.name }] : []),
    ...(selChapter ? [{ label: selChapter.title }] : []),
  ];

  const stepTitles = {
    1: { h: "Step 1 — अपनी कक्षा चुनें", p: "Select your Class" },
    2: { h: "Step 2 — विषय चुनें", p: `${selClass?.name} के विषय` },
    3: { h: "Step 3 — अध्याय चुनें", p: `${selClass?.name} · ${selSubject?.name} के अध्याय` },
    4: { h: "Step 4 — क्विज़ शुरू करें", p: `${selClass?.name} · ${selSubject?.name} · ${selChapter?.title}` },
  };

  return (
    <div className="vr-container my-8">
      <Seo
        title="MCQ Quiz — Bihar Board Class 9-12 Practice Tests"
        description="Chapter-wise MCQ क्विज़ — बिहार बोर्ड कक्षा 9 से 12 तक — तुरन्त परिणाम एवं व्याख्या के साथ।"
        canonical="/mcq"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "MCQ Quiz", url: "/mcq" }])}
      />
      <Breadcrumbs items={crumbTrail} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">MCQ Quiz</h1>
      <p className="text-slate-600 mt-2 font-hindi">Chapter-wise MCQ क्विज़ — तुरन्त परिणाम एवं व्याख्या।</p>

      {/* Step header + back */}
      <div className="mt-6 flex items-center gap-3" data-testid="quiz-step-header">
        {step > 1 && (
          <button onClick={goBack} className="inline-flex items-center gap-1 h-10 px-4 rounded-md border border-slate-200 bg-white font-semibold text-sm text-[#1E3A8A] hover:bg-blue-50" data-testid="quiz-step-back">
            <ArrowLeft size={16} /> Back
          </button>
        )}
        <div>
          <div className="font-display font-bold text-lg text-[#0F172A] font-hindi">{stepTitles[step].h}</div>
          <div className="text-xs text-slate-500 font-hindi">{stepTitles[step].p}</div>
        </div>
      </div>

      {loading && <div className="mt-8 text-slate-500">Loading...</div>}

      {/* STEP 1 — Classes */}
      {!loading && step === 1 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="quiz-step-1">
          {classes.map((c) => (
            <button key={c.id} onClick={() => pickClass(c)} className="vr-card p-4 sm:p-5 text-left hover:border-[#1E3A8A] group" data-testid={`quiz-class-${c.slug}`}>
              <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-black">{c.name.replace("Class ", "")}</div>
              <div className="mt-3 font-display font-bold text-lg text-[#0F172A]">{c.name}</div>
              <div className="text-sm text-slate-500 font-hindi">{c.hindi_name}</div>
              <div className="mt-3 flex items-center text-[#1E3A8A] font-semibold text-sm gap-1 group-hover:gap-2 transition-all">चुनें <ChevronRight size={14} /></div>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2 — Subjects */}
      {!loading && step === 2 && (
        <div className="mt-6" data-testid="quiz-step-2">
          {subjects.length === 0 && <div className="vr-card p-6 text-slate-500 text-sm">इस कक्षा में अभी कोई विषय उपलब्ध नहीं है।</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {subjects.map((s) => (
              <button key={s.id} onClick={() => pickSubject(s)} className="vr-card p-4 text-left hover:border-[#1E3A8A]" data-testid={`quiz-subject-${s.slug}`}>
                <div className="font-display font-bold text-[#0F172A]">{s.name}</div>
                <div className="text-xs text-slate-500 font-hindi">{s.hindi_name}</div>
                <div className="mt-3 flex items-center text-[#1E3A8A] font-semibold text-sm gap-1">चुनें <ChevronRight size={14} /></div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 3 — Chapters */}
      {!loading && step === 3 && (
        <div className="mt-6" data-testid="quiz-step-3">
          {chapters.length === 0 && <div className="vr-card p-6 text-slate-500 text-sm">इस विषय में अभी कोई अध्याय उपलब्ध नहीं है।</div>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {chapters.map((ch) => (
              <button key={ch.id} onClick={() => pickChapter(ch)} className="vr-card p-4 text-left hover:border-[#1E3A8A] flex items-start gap-3" data-testid={`quiz-chapter-${ch.slug}`}>
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center shrink-0"><BookOpen size={18} /></div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-[#0F172A] line-clamp-2">{ch.number ? `Ch. ${ch.number} — ` : ""}{ch.title}</div>
                  <div className="text-xs text-slate-500 font-hindi line-clamp-1">{ch.hindi_title}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 4 — Quizzes for the selected chapter */}
      {!loading && step === 4 && (
        <div className="mt-6" data-testid="quiz-step-4">
          {quizzes.length === 0 && <div className="vr-card p-6 text-slate-500 text-sm" data-testid="quiz-step-4-empty">इस अध्याय की अभी कोई क्विज़ उपलब्ध नहीं है।</div>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quizzes.map((q) => (
              <Link key={q.id} to={`/quiz/${q.slug}`} className="vr-card p-5" data-testid={`quizlist-${q.slug}`}>
                <div className="flex items-center gap-2 text-xs text-slate-500"><Clock size={12} /> {q.duration_minutes} मिनट</div>
                <h2 className="mt-2 font-display font-bold text-[#0F172A] text-base">{q.title}</h2>
                <div className="mt-1 text-sm text-slate-600 font-hindi line-clamp-2">{q.description}</div>
                <div className="mt-3 inline-flex items-center gap-1 bg-[#DC2626] text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Play size={12} /> Start Quiz</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlayerCore({ resource, slug, backTo, backLabel }) {
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [reveal, setReveal] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setError(""); setQuiz(null);
    api.get(`/${resource}/${slug}`).then((r) => { setQuiz(r.data); }).catch((e) => setError(e?.response?.status === 404 ? "यह quiz/test नहीं मिला।" : "लोड करने में त्रुटि।"));
  }, [resource, slug]);

  const questions = useMemo(() => quiz?.questions || [], [quiz]);
  const q = questions[idx];
  const score = useMemo(() => questions.reduce((s, qq) => {
    const chosen = answers[qq.id];
    const correct = qq.options?.findIndex((o) => o.is_correct);
    return s + (chosen === correct ? 1 : 0);
  }, 0), [questions, answers]);

  if (error) return (
    <div className="vr-container my-8 max-w-2xl">
      <Breadcrumbs items={[{ to: backTo, label: backLabel }, { label: "Not found" }]} />
      <div className="vr-card p-8 text-center" data-testid="quiz-notfound">
        <AlertTriangle size={40} className="mx-auto text-amber-600" />
        <h2 className="mt-3 font-display font-extrabold text-2xl text-[#0F172A]">{error}</h2>
        <Link to={backTo} className="vr-btn-primary mt-4 inline-flex">Back</Link>
      </div>
    </div>
  );
  if (!quiz) return <div className="vr-container my-10 text-slate-500">Loading...</div>;
  if (questions.length === 0) return (
    <div className="vr-container my-10 max-w-2xl">
      <Breadcrumbs items={[{ to: backTo, label: backLabel }, { label: quiz.title }]} />
      <div className="vr-card p-8 text-center text-slate-600">इस {resource === "tests" ? "test" : "quiz"} में अभी प्रश्न नहीं जोड़े गए हैं।</div>
    </div>
  );

  const pct = Math.round(((idx + 1) / questions.length) * 100);
  const chosen = answers[q.id];
  const correctIdx = q.options.findIndex((o) => o.is_correct);

  const choose = (i) => { if (reveal) return; setAnswers({ ...answers, [q.id]: i }); setReveal(true); };
  const next = () => { setReveal(false); if (idx + 1 >= questions.length) setDone(true); else setIdx(idx + 1); };
  const prev = () => { setReveal(false); setIdx(Math.max(0, idx - 1)); };
  const restart = () => { setAnswers({}); setIdx(0); setReveal(false); setDone(false); };

  if (done) {
    const percent = Math.round((score / questions.length) * 100);
    return (
      <div className="vr-container my-8 max-w-2xl">
        <Breadcrumbs items={[{ to: backTo, label: backLabel }, { label: "Result" }]} />
        <div className="vr-card p-8 text-center" data-testid="quiz-result">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Trophy size={40} /></div>
          <h2 className="mt-4 font-display font-extrabold text-3xl text-[#0F172A]">पूरा हुआ!</h2>
          <div className="mt-4 text-5xl font-display font-black text-[#1E3A8A]" data-testid="quiz-score">{score} / {questions.length}</div>
          <div className="mt-1 text-lg text-slate-600 font-hindi" data-testid="quiz-percent">{percent}% अंक</div>
          <div className="mt-6 flex justify-center gap-3">
            <button onClick={restart} className="vr-btn-primary" data-testid="quiz-restart"><RotateCcw size={16} /> Restart</button>
            <Link to={backTo} className="vr-btn-secondary">More</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vr-container my-8 max-w-3xl">
      <Seo
        title={`${quiz.title} — MCQ ${resource === "tests" ? "Test" : "Quiz"}`}
        description={quiz.description || `${quiz.title} — Bihar Board practice questions with instant result and explanation.`}
        canonical={`/${resource === "tests" ? "test" : "quiz"}/${quiz.slug}`}
        noindex
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: backLabel, url: backTo }, { name: quiz.title, url: `/${resource === "tests" ? "test" : "quiz"}/${quiz.slug}` }])}
      />
      <Breadcrumbs items={[{ to: backTo, label: backLabel }, { label: quiz.title }]} />
      <div className="vr-card p-6">
        <div className="flex items-center justify-between text-sm">
          <div className="font-bold text-[#1E3A8A]">Question {idx + 1} / {questions.length}</div>
          <div className="text-slate-500 flex items-center gap-1"><Clock size={14} /> {quiz.duration_minutes} min</div>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#DC2626]" style={{ width: `${pct}%` }} data-testid="quiz-progress" />
        </div>

        <h2 className="mt-6 font-display font-bold text-xl text-[#0F172A] font-hindi" data-testid="quiz-question">{q.question}</h2>

        <div className="mt-4 space-y-2">
          {q.options.map((o, i) => {
            const isChosen = chosen === i;
            const isCorrect = i === correctIdx;
            let cls = "border-slate-200 hover:border-[#1E3A8A]";
            if (reveal) {
              if (isCorrect) cls = "border-emerald-500 bg-emerald-50";
              else if (isChosen) cls = "border-red-500 bg-red-50";
            } else if (isChosen) cls = "border-[#1E3A8A] bg-blue-50";
            return (
              <button key={i} onClick={() => choose(i)} disabled={reveal} className={`w-full text-left p-4 border-2 rounded-lg flex items-center justify-between transition ${cls}`} data-testid={`quiz-option-${i}`}>
                <span className="font-hindi text-slate-800">{String.fromCharCode(65 + i)}. {o.text}</span>
                {reveal && isCorrect && <CheckCircle2 size={18} className="text-emerald-600" />}
                {reveal && isChosen && !isCorrect && <XCircle size={18} className="text-red-600" />}
              </button>
            );
          })}
        </div>

        {reveal && q.explanation && (
          <div className="mt-4 p-4 bg-blue-50 border-l-4 border-[#1E3A8A] rounded" data-testid="quiz-explanation">
            <div className="text-xs font-bold text-[#1E3A8A] uppercase mb-1">व्याख्या</div>
            <div className="text-sm text-slate-700 font-hindi">{q.explanation}</div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <button onClick={prev} disabled={idx === 0} className="inline-flex items-center gap-1 h-10 px-4 rounded-md border border-slate-200 disabled:opacity-40" data-testid="quiz-prev"><ChevronLeft size={16} /> Prev</button>
          <button onClick={next} disabled={!reveal} className="inline-flex items-center gap-1 h-10 px-5 rounded-md bg-[#1E3A8A] text-white font-semibold disabled:opacity-40" data-testid="quiz-next">
            {idx + 1 >= questions.length ? "Finish" : "Next"} <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function QuizPlayer() {
  const { slug } = useParams();
  return <PlayerCore resource="quizzes" slug={slug} backTo="/mcq-quiz" backLabel="MCQ Quiz" />;
}

export function TestPlayer() {
  const { slug } = useParams();
  return <PlayerCore resource="tests" slug={slug} backTo="/online-tests" backLabel="Online Tests" />;
}
